import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { ApiInterceptor, ClientInfoInterceptor, CleverlyCommonModule, LocaleService } from '@cleverly/shared/common';
import { ENV, IEnvironment } from '@cleverly/shared/core';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_API_ROUTES, CleverlyUrlGenerator } from '@cleverly/shared/data';
import { customApiUrls as tutorCustomApiUrls, entityConfig as tutorEntityConfig } from '@cleverly/tutor/data';
import { DefaultDataServiceConfig, EntityDataModule, HttpUrlGenerator, PLURAL_NAMES_TOKEN } from '@ngrx/data';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { customApiUrls, entityConfig } from '@cleverly/customer/data';
import { ApiAuthorizationModule, AuthService, FirebaseInitializationService } from '@cleverly/shared/api-authorization';
import { ErrorhandlerService } from '@cleverly/shared/common';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { RedirectComponent } from './components/redirect/redirect.component';

import { initializeApp, provideFirebaseApp, getApp, FirebaseApp } from '@angular/fire/app';
import { getAuth, indexedDBLocalPersistence, initializeAuth, provideAuth, connectAuthEmulator } from '@angular/fire/auth';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { environment } from '../environments/environment';
import { VerifyLoginComponent } from './components/verify-login/verify-login.component';
import { Capacitor } from '@capacitor/core';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import { registerLocaleData } from '@angular/common';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { providePrimeNG } from 'primeng/config';
import { getStoredPreset } from '../../../lib-design/theme-selection';
import { take, firstValueFrom } from 'rxjs';
import { AppLoadingService } from './services/app-loading.service';
import { provideAppInitializer, inject, NgZone } from '@angular/core';

registerLocaleData(localeDe);
registerLocaleData(localeEn);

// Global Firebase app instance
let firebaseApp: FirebaseApp | null = null;

// Initialize Firebase app - this must run first and complete before anything else
function initializeFirebaseApp(): Promise<FirebaseApp> {
    return new Promise((resolve, reject) => {
        try {
            if (firebaseApp) {
                resolve(firebaseApp);
                return;
            }

            firebaseApp = initializeApp(environment.firebaseConfig);
            resolve(firebaseApp);
        } catch (error) {
            console.error('Failed to initialize Firebase app:', error);
            reject(error);
        }
    });
}

@NgModule({
    declarations: [
        AppComponent,
        RedirectComponent,
        VerifyLoginComponent,
    ],
    imports: [
        // AuthModule,
        ApiAuthorizationModule,
        BrowserModule,
        BrowserAnimationsModule,
        StoreModule.forRoot(
            {},
            {
                runtimeChecks: {
                    strictStateImmutability: false,
                    strictActionImmutability: false,
                },
            }
        ),
        EffectsModule.forRoot([]),
        EntityDataModule.forRoot({
            entityMetadata: entityConfig.entityMetadata,
            pluralNames: entityConfig.pluralNames,
        }),
        StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
        AppRoutingModule,
        HttpClientModule,
        ProgressSpinnerModule,
        ToastModule,
        CleverlyCommonModule,
    ],
    exports: [],
    providers: [
        // FIRST: Initialize Firebase app before anything else
        provideAppInitializer(() => {
            return initializeFirebaseApp();
        }),
        
        // Basic environment and locale setup
        {
            provide: ENV,
            useFactory: () => environment,
        },
        LocaleService,
        {
            provide: LOCALE_ID,
            deps: [LocaleService],
            useFactory: (localeService: LocaleService) => localeService.locale,
        },
        
        // Google Tag Manager Configuration (injected for use in services)
        { provide: 'googleTagManagerId', useFactory: () => environment.gtmKey },
        { provide: 'googleTagManagerAuth', useFactory: () => environment.gtmAuth ?? '' },
        { provide: 'googleTagManagerPreview', useFactory: () => environment.gtmPreview ?? '' },
        
        // HTTP and API configuration
        { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true, deps: [ENV] },
        { provide: HTTP_INTERCEPTORS, useClass: ClientInfoInterceptor, multi: true, deps: [ENV] },
        {
            provide: DefaultDataServiceConfig,
            useFactory: (env: IEnvironment) => ({
                root: env.apiUrl + '/api/v1/',
            }),
            deps: [ENV],
        },
        { provide: PLURAL_NAMES_TOKEN, multi: true, useValue: { ...entityConfig.pluralNames, ...tutorEntityConfig.pluralNames } },
        { provide: CUSTOM_API_ROUTES, multi: true, useValue: { ...customApiUrls, ...tutorCustomApiUrls } },
        { provide: HttpUrlGenerator, useClass: CleverlyUrlGenerator },
        {
            provide: ErrorHandler,
            useClass: ErrorhandlerService,
        },
        
        // Firebase providers - these will use the already initialized app
        provideFirebaseApp(() => {
            if (!firebaseApp) {
                throw new Error('Firebase app not initialized! This should not happen.');
            }
            return firebaseApp;
        }),
        provideAuth(() => {
            if (!firebaseApp) {
                throw new Error('Firebase app not initialized! This should not happen.');
            }
            
            if (Capacitor.getPlatform() !== 'web') {
                return initializeAuth(firebaseApp, {
                    persistence: indexedDBLocalPersistence,
                });
            }
            
            return getAuth(firebaseApp);
        }),
        provideMessaging(() => {
            if (!firebaseApp) {
                throw new Error('Firebase app not initialized! This should not happen.');
            }
            return getMessaging(firebaseApp);
        }),
        
        // UI and other services
        MessageService,
        providePrimeNG({
            theme: {
                preset: getStoredPreset(),
                options: {
                    darkModeSelector: '.app-dark'
                }
            }
        }),
        
        // LAST: Initialize AuthService after Firebase is ready
        provideAppInitializer(() => {
            const authService = inject(AuthService);
            const loadingService = inject(AppLoadingService);
            const ngZone = inject(NgZone);
            
            return firstValueFrom(authService.getAccessToken$().pipe(take(1)))
                .finally(() => {
                    // Run the loading dismiss timer outside Angular zone
                    // to avoid a pending macrotask blocking navigation
                    ngZone.runOutsideAngular(() => {
                        setTimeout(() => {
                            ngZone.run(() => loadingService.setLoading(false));
                        }, 500);
                    });
                });
        }),
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
